import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Calendar, Clock, User, MapPin, FileText, 
  CheckCircle, XCircle, RefreshCw, Edit, 
  AlertTriangle, Stethoscope, Video, Phone
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';

const STATUS_CONFIG = {
  pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending', icon: Clock },
  scheduled: { color: 'bg-blue-100 text-blue-800', label: 'Scheduled', icon: Calendar },
  confirmed: { color: 'bg-green-100 text-green-800', label: 'Confirmed', icon: CheckCircle },
  in_progress: { color: 'bg-purple-100 text-purple-800', label: 'In Progress', icon: Stethoscope },
  completed: { color: 'bg-gray-100 text-gray-800', label: 'Completed', icon: CheckCircle },
  cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled', icon: XCircle },
  rescheduled: { color: 'bg-orange-100 text-orange-800', label: 'Rescheduled', icon: RefreshCw },
  no_show: { color: 'bg-red-100 text-red-800', label: 'No Show', icon: AlertTriangle }
};

const MODE_ICONS = {
  in_person: MapPin,
  telemedicine: Video,
  phone: Phone,
  both: Calendar
};

// Helper function to format provider display name avoiding title duplication
const formatProviderName = (title, fullName) => {
  if (!fullName) return '';
  
  // List of common title prefixes to check for duplication
  const titlePrefixes = ['Dr.', 'Dr', 'MD', 'DO', 'PhD', 'NP', 'PA', 'RN', 'PT', 'OD', 'DDS', 'DMD'];
  
  // Check if the name already starts with a title
  const nameLower = fullName.toLowerCase().trim();
  const startsWithTitle = titlePrefixes.some(prefix => 
    nameLower.startsWith(prefix.toLowerCase() + ' ') || 
    nameLower.startsWith(prefix.toLowerCase() + '.')
  );
  
  // If name already has a title, just return the name
  if (startsWithTitle) {
    return fullName;
  }
  
  // Otherwise, prepend the title
  return title ? `${title} ${fullName}` : fullName;
};

const AppointmentManagementDialog = ({ 
  appointment, 
  open, 
  onClose, 
  onUpdate,
  providers = [],
  services = []
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    appointment_date: '',
    appointment_time: '',
    duration_minutes: 30,
    provider_id: '',
    service_id: '',
    appointment_type: '',
    appointment_mode: 'in_person',
    notes: '',
    reason: ''
  });

  // Reschedule form state
  const [rescheduleForm, setRescheduleForm] = useState({
    new_date: '',
    new_time: '',
    new_provider_id: '',
    reason: ''
  });

  // Cancel form state
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    if (appointment) {
      const aptDate = new Date(appointment.appointment_date);
      setEditForm({
        appointment_date: aptDate.toISOString().split('T')[0],
        appointment_time: aptDate.toTimeString().slice(0, 5),
        duration_minutes: appointment.duration_minutes || 30,
        provider_id: appointment.provider_id || '',
        service_id: appointment.service_id || '',
        appointment_type: appointment.appointment_type || 'general',
        appointment_mode: appointment.appointment_mode || 'in_person',
        notes: appointment.notes || '',
        reason: appointment.reason || ''
      });
      
      // Reset reschedule form
      setRescheduleForm({
        new_date: '',
        new_time: '',
        new_provider_id: appointment.provider_id || '',
        reason: ''
      });
      setCancelReason('');
      setActiveTab('details');
    }
  }, [appointment]);

  if (!appointment) return null;

  const statusConfig = STATUS_CONFIG[appointment.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const ModeIcon = MODE_ICONS[appointment.appointment_mode] || Calendar;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Action handlers
  const handleConfirm = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/appointments/${appointment.id}/confirm`);
      toast.success('Appointment confirmed successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/appointments/${appointment.id}/complete?record_service_usage=true`);
      toast.success('Appointment marked as completed');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const dateTime = new Date(`${editForm.appointment_date}T${editForm.appointment_time}`);
      
      await axios.put(`${API}/appointments/${appointment.id}`, {
        appointment_date: dateTime.toISOString(),
        duration_minutes: parseInt(editForm.duration_minutes),
        provider_id: editForm.provider_id || null,
        service_id: editForm.service_id || null,
        appointment_type: editForm.appointment_type,
        appointment_mode: editForm.appointment_mode,
        notes: editForm.notes,
        reason: editForm.reason
      });
      
      toast.success('Appointment updated successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = async (e) => {
    e.preventDefault();
    if (!rescheduleForm.new_date || !rescheduleForm.new_time) {
      toast.error('Please select a new date and time');
      return;
    }
    
    setLoading(true);
    try {
      const newDateTime = new Date(`${rescheduleForm.new_date}T${rescheduleForm.new_time}`);
      
      await axios.post(`${API}/appointments/${appointment.id}/reschedule`, {
        new_date: newDateTime.toISOString(),
        new_provider_id: rescheduleForm.new_provider_id || null,
        reason: rescheduleForm.reason
      });
      
      toast.success('Appointment rescheduled successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/appointments/${appointment.id}/cancel`, {
        reason: cancelReason
      });
      
      toast.success('Appointment cancelled');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkNoShow = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/appointments/${appointment.id}`, {
        status: 'no_show'
      });
      toast.success('Appointment marked as No Show');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appointment');
    } finally {
      setLoading(false);
    }
  };

  const canEdit = !['cancelled', 'completed', 'rescheduled', 'no_show'].includes(appointment.status);
  const canConfirm = ['pending', 'scheduled'].includes(appointment.status);
  const canComplete = ['confirmed', 'scheduled', 'in_progress'].includes(appointment.status);
  const canCancel = !['cancelled', 'completed', 'rescheduled'].includes(appointment.status);
  const canReschedule = !['cancelled', 'completed', 'rescheduled', 'no_show'].includes(appointment.status);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">Manage Appointment</DialogTitle>
              <DialogDescription>
                {appointment.member?.full_name || 'Patient'} - {formatDate(appointment.appointment_date)}
              </DialogDescription>
            </div>
            <Badge className={statusConfig.color}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusConfig.label}
            </Badge>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="edit" disabled={!canEdit}>Edit</TabsTrigger>
            <TabsTrigger value="reschedule" disabled={!canReschedule}>Reschedule</TabsTrigger>
            <TabsTrigger value="cancel" disabled={!canCancel}>Cancel</TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Patient Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Name:</span> {appointment.member?.full_name || 'N/A'}</p>
                    <p><span className="text-gray-500">Patient ID:</span> {appointment.member?.patient_id || 'N/A'}</p>
                    <p><span className="text-gray-500">Email:</span> {appointment.member?.email || 'N/A'}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Appointment Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Date:</span> {formatDate(appointment.appointment_date)}</p>
                    <p><span className="text-gray-500">Time:</span> {formatTime(appointment.appointment_date)}</p>
                    <p><span className="text-gray-500">Duration:</span> {appointment.duration_minutes || 30} minutes</p>
                    <p className="flex items-center gap-1">
                      <span className="text-gray-500">Mode:</span> 
                      <ModeIcon className="h-4 w-4" />
                      {appointment.appointment_mode || 'In Person'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Stethoscope className="h-4 w-4" />
                    Service & Provider
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Service:</span> {appointment.service?.name || appointment.appointment_type || 'General'}</p>
                    <p><span className="text-gray-500">Provider:</span> {appointment.provider?.full_name || 'Not assigned'}</p>
                    {appointment.provider?.specialty && (
                      <p><span className="text-gray-500">Specialty:</span> {appointment.provider.specialty}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Notes & Reason
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Reason:</span> {appointment.reason || 'Not specified'}</p>
                    <p><span className="text-gray-500">Notes:</span> {appointment.notes || 'No notes'}</p>
                    {appointment.patient_notes && (
                      <p><span className="text-gray-500">Patient Notes:</span> {appointment.patient_notes}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              {canConfirm && (
                <Button onClick={handleConfirm} disabled={loading} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Appointment
                </Button>
              )}
              {canComplete && (
                <Button onClick={handleComplete} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Completed
                </Button>
              )}
              {canComplete && (
                <Button onClick={handleMarkNoShow} disabled={loading} variant="outline" className="text-orange-600 border-orange-600">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Mark as No Show
                </Button>
              )}
            </div>
          </TabsContent>

          {/* Edit Tab */}
          <TabsContent value="edit">
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={editForm.appointment_date}
                    onChange={(e) => setEditForm({...editForm, appointment_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input
                    type="time"
                    value={editForm.appointment_time}
                    onChange={(e) => setEditForm({...editForm, appointment_time: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Duration (minutes)</Label>
                  <Select 
                    value={editForm.duration_minutes.toString()} 
                    onValueChange={(v) => setEditForm({...editForm, duration_minutes: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                      <SelectItem value="90">90 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Appointment Mode</Label>
                  <Select 
                    value={editForm.appointment_mode} 
                    onValueChange={(v) => setEditForm({...editForm, appointment_mode: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_person">In Person</SelectItem>
                      <SelectItem value="telemedicine">Telemedicine</SelectItem>
                      <SelectItem value="phone">Phone Call</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Provider</Label>
                  <Select 
                    value={editForm.provider_id || 'none'} 
                    onValueChange={(v) => setEditForm({...editForm, provider_id: v === 'none' ? '' : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific provider</SelectItem>
                      {providers.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {formatProviderName(p.title, p.full_name)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Service</Label>
                  <Select 
                    value={editForm.service_id || 'none'} 
                    onValueChange={(v) => setEditForm({...editForm, service_id: v === 'none' ? '' : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">General appointment</SelectItem>
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Appointment Type</Label>
                <Select 
                  value={editForm.appointment_type} 
                  onValueChange={(v) => setEditForm({...editForm, appointment_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="consultation">Consultation</SelectItem>
                    <SelectItem value="follow_up">Follow Up</SelectItem>
                    <SelectItem value="checkup">Checkup</SelectItem>
                    <SelectItem value="procedure">Procedure</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Reason for Visit</Label>
                <Input
                  value={editForm.reason}
                  onChange={(e) => setEditForm({...editForm, reason: e.target.value})}
                  placeholder="Reason for the appointment"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Internal notes about the appointment"
                  rows={3}
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="bg-teal-600 hover:bg-teal-700">
                  <Edit className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Reschedule Tab */}
          <TabsContent value="reschedule">
            <form onSubmit={handleReschedule} className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  Rescheduling will mark the current appointment as &quot;Rescheduled&quot; and create a new one with the new date/time.
                  The patient will be notified of this change.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>New Date *</Label>
                  <Input
                    type="date"
                    value={rescheduleForm.new_date}
                    onChange={(e) => setRescheduleForm({...rescheduleForm, new_date: e.target.value})}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label>New Time *</Label>
                  <Input
                    type="time"
                    value={rescheduleForm.new_time}
                    onChange={(e) => setRescheduleForm({...rescheduleForm, new_time: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>New Provider (optional)</Label>
                <Select 
                  value={rescheduleForm.new_provider_id || 'same'} 
                  onValueChange={(v) => setRescheduleForm({...rescheduleForm, new_provider_id: v === 'same' ? '' : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keep same provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same">Keep same provider</SelectItem>
                    {providers.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {formatProviderName(p.title, p.full_name)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Reason for Rescheduling</Label>
                <Textarea
                  value={rescheduleForm.reason}
                  onChange={(e) => setRescheduleForm({...rescheduleForm, reason: e.target.value})}
                  placeholder="Why is this appointment being rescheduled?"
                  rows={3}
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reschedule Appointment
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Cancel Tab */}
          <TabsContent value="cancel">
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  Cancelling an appointment cannot be undone. The patient will be notified of the cancellation.
                </p>
              </div>

              <div>
                <Label>Cancellation Reason *</Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please provide a reason for cancellation..."
                  rows={4}
                  required
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" onClick={onClose}>
                  Keep Appointment
                </Button>
                <Button 
                  onClick={handleCancel} 
                  disabled={loading || !cancelReason.trim()} 
                  className="bg-red-600 hover:bg-red-700"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Appointment
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentManagementDialog;
